/**
 * Owns rotation state of the floating CSS-3D card.
 * `pivot` carries user rotation; `root` runs the idle keyframes animation
 * which we pause during interactions or page transitions.
 */
export interface CardElements {
  root: HTMLElement;
  pivot: HTMLElement;
}

export class CardController {
  readonly root: HTMLElement;
  readonly pivot: HTMLElement;
  private rotX = 0;
  private rotY = 0;
  private rotZ = 0;
  private tx = 0;
  private ty = 0;
  private scl = 1;
  externalControl = false;

  constructor(elements: CardElements) {
    this.root = elements.root;
    this.pivot = elements.pivot;
    this.applyRotation();
  }

  setRotation(x: number, y: number, z?: number): void {
    this.rotX = x;
    this.rotY = y;
    if (z !== undefined) this.rotZ = z;
    this.applyRotation();
  }

  addRotation(dx: number, dy: number, dz = 0): void {
    this.rotY += dx;
    this.rotX += dy;
    this.rotZ += dz;
    this.applyRotation();
  }

  get rotation(): { x: number; y: number; z: number } {
    return { x: this.rotX, y: this.rotY, z: this.rotZ };
  }

  setTransform(tx: number, ty: number, scl: number): void {
    this.tx = tx;
    this.ty = ty;
    this.scl = scl;
    this.applyRotation();
  }

  resetTransform(): void {
    this.setTransform(0, 0, 1);
  }

  get transform(): { tx: number; ty: number; scl: number } {
    return { tx: this.tx, ty: this.ty, scl: this.scl };
  }

  beginDrag(): void {
    this.pivot.classList.add("is-dragging");
  }

  endDrag(): void {
    this.pivot.classList.remove("is-dragging");
  }

  suspendFloat(): void {
    this.root.classList.add("is-suspended-float");
  }

  resumeFloat(): void {
    this.root.classList.remove("is-suspended-float");
  }

  private applyRotation(): void {
    const xDeg = (this.rotX * 180) / Math.PI;
    const yDeg = (this.rotY * 180) / Math.PI;
    const zDeg = (this.rotZ * 180) / Math.PI;
    this.pivot.style.transform =
      `translate3d(${this.tx.toFixed(2)}px, ${this.ty.toFixed(2)}px, 0) ` +
      `scale(${this.scl.toFixed(3)}) ` +
      `rotateY(${yDeg.toFixed(3)}deg) rotateX(${xDeg.toFixed(3)}deg) rotateZ(${zDeg.toFixed(3)}deg)`;
    this.pivot.style.setProperty("--tilt-x", xDeg.toFixed(2));
    this.pivot.style.setProperty("--tilt-y", yDeg.toFixed(2));
  }
}
